'use client'
import { forwardRef, ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  loading?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  fullWidth?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      iconLeft,
      iconRight,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = `
      inline-flex items-center justify-center gap-2
      font-medium transition-all duration-200 rounded-lg
      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orion-accent focus-visible:ring-offset-2 focus-visible:ring-offset-orion-bg
      disabled:opacity-50 disabled:cursor-not-allowed
      ${fullWidth ? 'w-full' : ''}
    `

    const variants = {
      primary: `
        bg-orion-accent text-orion-bg hover:bg-orion-accentHover
        px-6 py-3 text-sm uppercase tracking-wider
        shadow-glow-accent-subtle hover:shadow-glow-accent
        active:scale-[0.98]
      `,
      secondary: `
        bg-orion-card text-orion-text border border-orion-border
        hover:border-orion-accent/50 hover:text-orion-accent hover:bg-orion-highlight/50
        px-6 py-3 text-sm uppercase tracking-wider
      `,
      outline: `
        border border-orion-border text-orion-text
        hover:border-orion-accent hover:text-orion-accent hover:bg-orion-accent/5
        px-6 py-3 text-sm uppercase tracking-wider
      `,
      ghost: `
        bg-transparent text-orion-muted hover:text-orion-text hover:bg-orion-highlight/50
        px-4 py-2 text-sm
      `,
      danger: `
        bg-orion-error text-white hover:bg-red-600
        px-6 py-3 text-sm uppercase tracking-wider
        shadow-glow-error hover:shadow-glow-error
      `,
    }

    const sizes = {
      sm: 'px-3 py-1.5 text-xs',
      md: 'px-5 py-2.5 text-sm',
      lg: 'px-7 py-3.5 text-base',
      xl: 'px-10 py-4 text-lg',
    }

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || loading}
        aria-busy={loading}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Loading...</span>
          </>
        ) : (
          <>
            {iconLeft && <span className="flex-shrink-0">{iconLeft}</span>}
            {children}
            {iconRight && <span className="flex-shrink-0">{iconRight}</span>}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'